import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { and, or } from 'ember-truth-helpers';
import { not } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsLinkInline,
  HdsButton,
  HdsTable,
  HdsIcon,
  HdsDropdown,
  HdsApplicationState,
  HdsTextBody,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  CoreModalConfirm,
} from 'core/components';
import { rbacRoles, optionForSourceState } from 'core/helpers';
import { boxMargin, boxPadding } from 'core/modifiers';
import PolicyManager from 'manage-access/utils/policy-manager';
import TerraformPermissions from 'hcp/components/hcp/alert/terraform-permissions';

/**
 * Component for listing organization groups with project-level IAM policy role bindings and their members.
 *
 * @class PageGroupsProjectList
 *
 * @argument {Array.<Object>} groups - All of the groups at an organization level.
 * @argument {Array.<Object>} project - The project object.
 * @argument {Array.<Object>} projectGroupsToMembersListBindings - Bindings of groups with a project-level IAM policy binding to their group members list.
 *
 * Example usage:
 * ```
 * <Page::Groups::Project::List
 *   @groups={{@model.groups}}
 *   @project={{@model.project}}
 *   @projectGroupsToMembersListBindings={{@model.projectGroupsToMembersListBindings}}
 * />
 * ```
 */
export default class PageGroupsProjectList extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked showRemoveModal = false;
  @tracked groupToRemove = ''; // TODO: Don't initialize this as a String, it will become a Group object later, null might be better.

  /**
   * Removes a group binding from a policy and attempts to update the policy.
   * If the policy has been updated concurrently, it will retry up to maxRetries.
   */
  remove = task(async () => {
    const projectId = this.args.project.id;
    const resourceId = this.groupToRemove.resourceId;
    const maxRetries = 3;

    const { updated } = await this.removeGroupFromPolicy(
      projectId,
      resourceId,
      maxRetries
    );

    if (updated) {
      this.flashMessages.success(
        this.intl.t(
          `components.page.access-control.groups.remove.modal.success.title`
        ),
        {
          content: this.intl.t(
            `components.page.access-control.groups.remove.modal.success.content`,
            {
              groupName: this.groupToRemove.displayName,
              projectName: this.args.project.name,
              htmlSafe: true,
            }
          ),
        }
      );
      this.refreshRoute();
    } else {
      this.flashMessages.error(
        this.intl.t(
          `components.page.access-control.groups.remove.modal.error.title`
        ),
        {
          content: this.intl.t(
            `components.page.access-control.groups.remove.modal.error.content`,
            {
              groupName: this.groupToRemove.displayName,
              projectName: this.args.project.name,
              htmlSafe: true,
            }
          ),
        }
      );
    }
    this.resetRemoveGroup();
  });

  async removeGroupFromPolicy(projectId, resourceId, maxRetries) {
    for (let i = 0; i < maxRetries; i++) {
      try {
        // Get the latest policy in order to get the latest etag to protect
        // from concurrent updates.
        const { policy } =
          await this.api.resourceManager.project.projectServiceGetIamPolicy(
            projectId
          );

        if (!policy) {
          throw new Error();
        }

        let policyToUpdate;
        let updatedPolicy;

        // Given the latest policy, attempt to remove the group
        policyToUpdate = new PolicyManager({ policy });
        const { memberType, roleIds = [] } =
          policyToUpdate.getRolesByMemberId(resourceId) ?? {};
        roleIds.forEach((roleId) => {
          policyToUpdate.removeMemberFromRole(
            { memberType, memberId: resourceId },
            [roleId]
          );
        });
        updatedPolicy = policyToUpdate.getRawPolicy();

        const policyPayload = {
          policy: {
            ...updatedPolicy,
          },
        };

        await this.api.resourceManager.project.projectServiceSetIamPolicy(
          projectId,
          policyPayload
        );

        return {
          updated: true,
        };
      } catch (e) {
        // A conflict indicates a concurrent update.
        // We may just need try again.
        if (e.status === 409) {
          continue;
        }

        return {
          updated: false,
        };
      }
    }

    return {
      updated: false,
    };
  }

  confirmRemoveGroup = task(async (group) => {
    this.groupToRemove = group;
    this.showRemoveModal = true;
  });

  resetRemoveGroup = () => {
    this.showRemoveModal = false;
  };

  refreshRoute = () => {
    let route = getOwner(this).lookup(
      `route:${this.router.currentRoute.parent.name}`
    );
    return route.refresh();
  };

  get hasGroupsInOrg() {
    return this.args.groups?.length;
  }
  get hasprojectGroupsToMembersListBindings() {
    return this.args.projectGroupsToMembersListBindings?.length;
  }

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |P|>
          <P.Title>
            {{t "components.page.access-control.groups.list.title"}}
          </P.Title>
          <P.Breadcrumb>
            <BreadcrumbsContainer />
          </P.Breadcrumb>
          <P.IconTile @icon="users" />
          <P.Description>
            {{t "components.page.access-control.groups.list.description"}}
            <HdsLinkInline
              @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/groups"
              @icon="docs-link"
              @isHrefExternal={{true}}
              data-test-description-external-link
            >
              {{t "components.page.access-control.groups.list.cta"}}
            </HdsLinkInline>
          </P.Description>
          {{#if
            (and
              @hasGroupsToAdd
              (can "get-iam-policy in project")
              (can "set-iam-policy in project")
            )
          }}
            <P.Actions>
              <HdsButton
                @color="primary"
                @icon="plus"
                @iconPosition="leading"
                @route="cloud.access-control.groups.add"
                @text={{t "components.page.access-control.groups.add.button"}}
                data-test-page-groups-project-list-header-action
              />
            </P.Actions>
          {{/if}}
        </HdsPageHeader>
      </:header>
      <:alert>
        <TerraformPermissions as |Controls|>
          <Controls.GoToTerraform />
        </TerraformPermissions>
      </:alert>
      <:body>
        <div data-test-page-groups-project-list>
          {{! Has groups in project, show table }}
          {{#if this.hasprojectGroupsToMembersListBindings}}
            <HdsTable
              @caption={{t "components.page.access-control.groups.list.title"}}
              @valign="top"
              data-test-groups-list-table
            >
              <:head as |H|>
                <H.Tr>
                  <H.Th>
                    {{t
                      "components.page.access-control.groups.list.table.header.group-name"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.access-control.groups.list.table.header.description"
                    }}
                  </H.Th>
                  <H.Th @width="50px">
                    {{t
                      "components.page.access-control.groups.list.table.header.members"
                    }}
                  </H.Th>
                  <H.Th @width="220px">
                    {{t
                      "components.page.access-control.groups.list.table.header.role"
                    }}
                  </H.Th>
                  <H.Th @width="220px">
                    {{t
                      "components.page.access-control.groups.list.table.header.scope"
                    }}
                  </H.Th>
                  {{#if (can "delete groups")}}
                    <H.Th>
                      {{t
                        "components.page.access-control.groups.list.table.header.actions"
                      }}
                    </H.Th>
                  {{/if}}
                </H.Tr>
              </:head>
              <:body as |B|>
                {{#each
                  @projectGroupsToMembersListBindings key="id"
                  as |binding|
                }}
                  <B.Tr data-test-group-row={{binding.displayName}}>
                    <B.Td data-test-group-item-name>
                      <HdsLinkInline
                        @color="secondary"
                        @route="cloud.access-control.groups.detail"
                        @model={{binding.resourceName}}
                        class="hds-font-weight-medium"
                      >
                        {{binding.displayName}}
                      </HdsLinkInline>
                    </B.Td>
                    <B.Td data-test-group-item-description>
                      {{binding.description}}
                    </B.Td>
                    <B.Td data-test-group-item-member-count>
                      {{binding.memberCount}}
                    </B.Td>
                    <B.Td data-test-group-item-role>
                      <Flex @direction="column" @gap="xs">
                        {{#each binding.roleLocations as |roleLocation|}}
                          <span>
                            {{#let
                              (rbacRoles
                                roleLocation.roleId
                                active=true
                                type=roleLocation.link.type
                              )
                              as |rbacRole|
                            }}
                              {{or
                                rbacRole.label
                                roleLocation.title
                                roleLocation.roleId
                                "—"
                              }}
                            {{/let}}
                          </span>
                        {{else}}
                          —
                        {{/each}}
                      </Flex>
                    </B.Td>
                    <B.Td data-test-group-item-scope>
                      <Flex @direction="column" @gap="xs">
                        {{#each binding.roleLocations as |roleLocation|}}
                          {{#let
                            (optionForSourceState
                              "resource-type" roleLocation.link.type
                            )
                            as |option|
                          }}
                            <Flex
                              @alignItems="start"
                              @direction="row"
                              @gap="xs"
                            >
                              <span>
                                <HdsIcon
                                  @name={{option.icon}}
                                  @isInline={{true}}
                                />
                              </span>
                              <span>{{or
                                  roleLocation.link.name
                                  roleLocation.member.memberId
                                }}</span>
                            </Flex>
                          {{/let}}
                        {{/each}}
                      </Flex>
                    </B.Td>
                    {{#if (can "delete group")}}
                      <B.Td data-test-group-item-cell>
                        <Flex @justifyContent="end">
                          <HdsDropdown data-test-more-menu as |dd|>
                            <dd.ToggleIcon
                              @icon="more-horizontal"
                              @text={{t "components.form.more"}}
                              @hasChevron={{false}}
                              data-test-group-more-menu-trigger
                            />
                            <dd.Interactive
                              @icon="edit"
                              @text={{t
                                "components.page.access-control.groups.list.actions.edit-role"
                              }}
                              @route="cloud.access-control.groups.detail.edit"
                              @model={{binding.resourceName}}
                            />
                            <dd.Interactive
                              @color="critical"
                              @icon="trash"
                              @text={{t
                                "components.page.access-control.groups.list.actions.remove"
                              }}
                              {{on
                                "click"
                                (fn this.confirmRemoveGroup.perform binding)
                              }}
                              data-test-group-confirm-removal
                            />
                          </HdsDropdown>
                        </Flex>
                      </B.Td>
                    {{/if}}
                  </B.Tr>
                {{/each}}
              </:body>
            </HdsTable>
          {{else if (and (can "set-iam-policy in project") @hasGroupsToAdd)}}
            {{! Prompt user to add groups to project. }}
            <div {{boxPadding "2xl 0 0 0"}}>
              <HdsApplicationState as |A|>
                <A.Header
                  @title={{t
                    "components.page.access-control.groups.list.zero-state.zero-groups-in-project.header"
                  }}
                  data-test-page-groups-project-list-zero-groups-in-project-header
                />
                <A.Body
                  @text={{t
                    "components.page.access-control.groups.list.zero-state.zero-groups-in-project.description"
                  }}
                  data-test-page-groups-project-list-zero-groups-in-project-body
                />
                <A.Footer as |F|>
                  <F.LinkStandalone
                    @icon="plus"
                    @iconPosition="leading"
                    @route="cloud.access-control.groups.add"
                    @text={{t
                      "components.page.access-control.groups.add.button"
                    }}
                    data-test-page-groups-project-list-zero-add
                  />
                </A.Footer>
              </HdsApplicationState>
            </div>
          {{else if
            (and (can "set-iam-policy in project") (not this.hasGroupsInOrg))
          }}
            {{! Prompt user to create groups at org level. }}
            <div {{boxPadding "2xl 0 0 0"}}>
              <HdsApplicationState as |A|>
                <A.Header
                  @title={{t
                    "components.page.access-control.groups.list.zero-state.zero-groups-in-organization.header"
                  }}
                  data-test-page-groups-project-list-no-groups-in-org-header
                />
                <A.Body
                  @text={{t
                    "components.page.access-control.groups.list.zero-state.zero-groups-in-organization.description"
                  }}
                  data-test-page-groups-project-list-no-groups-in-org-body
                />
              </HdsApplicationState>
            </div>
          {{else}}
            {{! Doesn't have permissions to add groups at all. Show 'talk to admin' message. }}
            <div {{boxPadding "2xl 0 0 0"}}>
              <HdsApplicationState as |A|>
                <A.Header
                  @title={{t
                    "components.page.access-control.groups.list.zero-state.zero-groups-in-organization.header"
                  }}
                  data-test-page-groups-project-list-zero-header
                />
                <A.Body
                  @text={{t
                    "components.page.access-control.groups.list.viewer.description"
                  }}
                  data-test-page-groups-project-list-zero-body
                />
              </HdsApplicationState>
            </div>
          {{/if}}
        </div>
      </:body>
    </CoreLayoutMain>

    {{#if this.showRemoveModal}}
      <CoreModalConfirm
        @confirmButtonText={{t
          "components.page.access-control.groups.remove.modal.tagline"
        }}
        @confirmHelp={{t
          "components.page.access-control.groups.remove.modal.help-text"
        }}
        @confirmText={{t
          "components.page.access-control.groups.remove.modal.remove"
        }}
        @headerIcon="alert-triangle"
        @headerTagline={{t
          "components.page.access-control.groups.remove.modal.tagline"
        }}
        @headerText={{t
          "components.page.access-control.groups.remove.modal.header"
          groupName=this.groupToRemove.displayName
          htmlSafe=true
        }}
        @onCancel={{this.resetRemoveGroup}}
        @onSubmit={{this.remove}}
      >
        <:body>
          <Flex
            @alignItems="start"
            @direction="column"
            @gap="2xs"
            @tag="p"
            {{boxMargin "0 0 md 0"}}
          >
            <HdsTextBody @size="200" @weight="semibold" @color="strong">
              {{t "components.page.access-control.groups.remove.modal.title"}}
            </HdsTextBody>
            <HdsTextBody
              @size="200"
              @weight="regular"
              @color="primary"
              @tag="p"
            >
              {{t
                "components.page.access-control.groups.remove.modal.body"
                groupName=this.groupToRemove.displayName
                projectName=@project.name
                htmlSafe=true
              }}
            </HdsTextBody>
          </Flex>
        </:body>
      </CoreModalConfirm>
    {{/if}}
  </template>
}
